// frontend/src/components/setup/BuildStep.tsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/engagement";
import { cn } from "@/lib/utils";
import { useScoringStore } from "@/stores/scoring-store";
import type { Concept } from "@/types/concept";
import { type BuildStats, useStartSetup } from "./useStartSetup";

/** `cancelled` is neutral (the user stopped it), not an error. */
type CardState = "pending" | "running" | "done" | "failed" | "cancelled";
interface CardView { state: CardState; big: string; line: string }

/** The grading card's view of *this build's* scoring run (spec §2a). */
interface Owned { isScoring: boolean; isCompleted: boolean; error: string | null; background: boolean; scored: number; total: number }

function gradingCard(stats: BuildStats, run: Owned, seenScoring: boolean, buildDone: boolean): CardView {
  if (stats.completedAt == null) {
    return { state: "pending", big: "Next", line: `~${stats.inYears ?? "…"} papers, 0–100 · runs in the background` };
  }
  // Setup completes before the hook lists what to grade (a slow fetch): until the
  // build is done, "not started" means "not yet", not "nothing to grade".
  if (!stats.scoringStarted) {
    return buildDone ? { state: "done", big: "0", line: "Nothing new to grade" } : { state: "running", big: "…", line: "Starting…" };
  }
  // Another concept's run held the (global, unscoped) store: we can't tell ours from it, so
  // claim nothing about it and point at the bar.
  if (stats.scoringQueued) return { state: "done", big: "—", line: "Runs in the background · progress in the bar at the bottom" };
  if (run.error) return { state: "failed", big: "—", line: "Grading stopped — you can restart it from Curate" };
  if (run.background) return { state: "done", big: String(run.scored), line: `${run.scored} graded · the rest finish in the background` };
  if (run.isCompleted) return { state: "done", big: String(run.scored), line: "graded" };
  if (run.isScoring) return { state: "running", big: `${run.scored} / ${run.total}`, line: "graded · runs in the background" };
  // Idle: startScoring may not have opened its job yet — or the run was reset (cancelled).
  return seenScoring
    ? { state: "cancelled", big: "—", line: "Grading cancelled" }
    : { state: "running", big: "…", line: "Starting…" };
}

export function BuildStep({ concept, onFailed, onRetry, onBack }: {
  concept: Concept;
  /** The build ended in `error` / `no_network`: the page clears its started flag. */
  onFailed: () => void;
  /** Try again: the page marks the build started again. */
  onRetry: () => void;
  /** Back to seeds — offered only in failure states. */
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const { start, state, stats } = useStartSetup(concept);
  const isScoring = useScoringStore((s) => s.isScoring);
  const isCompleted = useScoringStore((s) => s.isCompleted);
  const error = useScoringStore((s) => s.error);
  const background = useScoringStore((s) => s.backgroundBatch !== null);
  const scored = useScoringStore((s) => s.progress.scored);
  const total = useScoringStore((s) => s.progress.total);
  const owned = stats.scoringStarted && !stats.scoringQueued;
  const run: Owned = { isScoring, isCompleted, error, background, scored, total };

  // Refs survive StrictMode's simulated remount, so this starts exactly once.
  const started = useRef(false);
  useEffect(() => {
    if (!started.current) { started.current = true; void start(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on mount
  }, []);

  // Idle right after startScoring = "Starting…"; idle after we've seen it run = cancelled.
  const [seenScoring, setSeenScoring] = useState(false);
  useEffect(() => {
    if (!owned) setSeenScoring(false);
    else if (isScoring) setSeenScoring(true);
  }, [owned, isScoring]);

  const failed = state === "error" || state === "no_network";
  useEffect(() => {
    if (failed) onFailed();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per failure, not per render
  }, [state]);

  const seedWord = `${stats.seeds} seed${stats.seeds === 1 ? "" : "s"}`;
  const followed: CardView = stats.connected !== null
    ? { state: "done", big: String(stats.connected), line: `papers connected to your ${seedWord}` }
    : stats.seeds > 0
      ? { state: "running", big: "…", line: `Looking at seed ${Math.min(stats.seedsDone + 1, stats.seeds)} of ${stats.seeds}…` }
      : { state: "running", big: "…", line: "Getting your seeds…" };
  const hiddenPart = stats.hidden ? ` · ${stats.hidden} more kept hidden` : "";
  const adding: CardView = stats.connected === null
    ? { state: "pending", big: "Next", line: "Papers in your years land here" }
    : { state: stats.completedAt != null ? "done" : "running", big: `${stats.savedInYears} / ${stats.inYears ?? 0}`, line: `in your years${hiddenPart}` };
  const grading = gradingCard(stats, run, seenScoring, state === "done");
  // A failed build stops whichever card was running.
  const stop = (c: CardView): CardView => (failed && c.state === "running" ? { ...c, state: "failed", line: "Stopped" } : c);

  function openCurate() {
    trackEvent("setup_curate_opened", {
      concept_id: concept.id,
      waited_ms: stats.completedAt != null ? Date.now() - stats.completedAt : null,
      graded: owned ? scored : null,
      total: owned ? total : null,
      flow: "boards",
    });
    void navigate({ to: "/concepts/$conceptId/curate", params: { conceptId: concept.id }, replace: true });
  }

  return (
    <section className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold">Building {concept.name}</h1>
        <p className="text-muted-foreground">You can start reviewing as soon as papers land. Grading carries on in the background.</p>
      </div>

      <div role="status" aria-live="polite" className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StageCard label="Followed citations" view={stop(followed)} />
        <StageCard label="Adding to your concept" view={stop(adding)}
          progress={stats.inYears ? { done: stats.savedInYears, total: stats.inYears } : undefined} />
        <StageCard label="Grading" view={stop(grading)} />
      </div>

      {stats.recent.length > 0 && (
        <div className="rounded-lg border bg-card">
          <div className="flex items-baseline justify-between gap-3 border-b border-border/60 px-4 py-2">
            <span className="text-sm font-medium">Just added</span>
            <span className="text-xs text-muted-foreground">{stats.savedInYears} so far</span>
          </div>
          <ul className="flex flex-col divide-y divide-border/60">
            {stats.recent.map((p, i) => (
              <li key={`${i}-${p.title}`} className="flex flex-col gap-0.5 px-4 py-2">
                <span className="text-sm">{p.title}</span>
                <span className="text-xs text-muted-foreground">{p.relation}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {state === "error" && <p role="alert" className="text-destructive">Couldn't start. Try again.</p>}
      {state === "no_network" && (
        <p role="alert" className="text-destructive">
          We couldn't reach what any of your seeds cite or are cited by. Try again in a minute, or go back and pick different seeds.
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {failed && <Button variant="outline" onClick={onBack}>Back to seeds</Button>}
        {failed && <Button variant="outline" onClick={() => { onRetry(); void start(); }}>Try again</Button>}
        <span className="grow" />
        <span className="text-sm text-muted-foreground">Grading progress follows you in the bar at the bottom</span>
        <Button size="lg" onClick={openCurate} disabled={state !== "done"}>Open Curate now</Button>
      </div>
    </section>
  );
}

function StageCard({ label, view, progress }: { label: string; view: CardView; progress?: { done: number; total: number } }) {
  const pct = progress ? Math.round((100 * Math.min(progress.done, progress.total)) / progress.total) : null;
  return (
    <div data-state={view.state}
      className={cn("flex flex-col gap-1 rounded-lg border bg-card px-4 py-3",
        view.state === "running" && "border-primary/40 bg-primary/10")}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {view.state === "running" && <Loader2 aria-hidden className="size-4 shrink-0 text-primary motion-safe:animate-spin" />}
        {view.state === "done" && <Check aria-hidden className="size-4 shrink-0 text-green-400" />}
      </div>
      <span className={cn("text-2xl font-semibold tabular-nums", (view.state === "pending" || view.state === "cancelled") && "text-muted-foreground")}>{view.big}</span>
      <span className={cn("text-xs text-muted-foreground", view.state === "failed" && "text-destructive/80")}>{view.line}</span>
      {progress && pct !== null && (
        <div role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.done}
          className="mt-1 h-1 overflow-hidden rounded-full bg-primary/15">
          <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}
